import React, { useState, useEffect } from "react";
import Device from "./Device";
import { subscribeUserToPush } from "../push";
import AlertPopover from "./AlertPopover";

const DeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batteryThreshold, setBatteryThreshold] = useState(20);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const handleDeviceSelect = (deviceId, isSelected) => {
    setSelectedDevices((prev) => {
      const newSelected = new Set(prev);
      if (isSelected) {
        newSelected.add(deviceId);
      } else {
        newSelected.delete(deviceId);
      }
      return newSelected;
    });
  };

  const handleTestNotification = async () => {
    try {
      const subscription = await subscribeUserToPush();

      if (!subscription) {
        setAlertMessage("Push subscription not found or permission denied.");
        setShowAlert(true);
        return;
      }

      const response = await fetch("/api/test-notification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(subscription.toJSON()),
      });

      if (response.ok) {
        setAlertMessage("Test notification sent! Check your device.");
        setShowAlert(true);
      } else {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to send test notification");
      }
    } catch (error) {
      console.error("Test notification failed:", error);
      setAlertMessage(`Failed to send test notification: ${error}`);
      setShowAlert(true);
    }
  };

  const handleSubscribe = async () => {
    try {
      const deviceList = devices
        .filter((device) => selectedDevices.has(device.id))
        .map((device) => ({
          id: device.id,
          name: device.name,
          battery_level: device.batteryLevel,
          last_seen: device.last_seen,
        }));

      const subscription = await subscribeUserToPush();

      if (subscription) {
        const response = await fetch("/api/subscribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            devices: deviceList,
            threshold: batteryThreshold,
            subscription: subscription.toJSON(),
          }),
        });

        if (response.ok) {
          if (deviceList.length <= 0) {
            setAlertMessage(`Your notifications have been disabled`);
            setShowAlert(true);
          } else {
            setAlertMessage(
              `Successfully subscribed to battery notifications!\n\nYou'll be notified when battery drops below ${batteryThreshold}%`,
            );
          }

          await fetchDevices();
        } else {
          console.error("Server failed to save subscription");
          setAlertMessage("Failed to save subscription");
        }
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Subscription failed:", error);
      setAlertMessage(`Failed to subscribe to notifications: ${error}`);
      setShowAlert(true);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await fetch("/api/devices", {
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
          return;
        }
        if (response.status === 409) {
          const data = await response.json();
          throw new Error(data.detail);
        }
        throw new Error(`Failed to fetch devices: ${response.statusText}`);
      }

      const data = await response.json();
      setDevices(data.devices);
      setBatteryThreshold(data.threshold);

      const subscribedDevices = data.devices
        .filter((device) => device.subscribed)
        .map((device) => device.id);
      setSelectedDevices(new Set(subscribedDevices));
    } catch (err) {
      console.error("Error fetching devices:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const getThresholdColor = (threshold) => {
    if (threshold <= 10) return "#dc3545";
    if (threshold <= 25) return "#fd7e14";
    if (threshold <= 50) return "#ffc107";
    return "#28a745";
  };

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          fontSize: "18px",
          color: "#666",
        }}
      >
        <div
          style={{
            display: "inline-block",
            padding: "20px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          📱 Loading your devices...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          margin: "20px auto",
          maxWidth: "500px",
          textAlign: "center",
          padding: "30px",
          color: "#dc3545",
          border: "2px solid #dc3545",
          borderRadius: "12px",
          backgroundColor: "#f8d7da",
        }}
      >
        <h3 style={{ margin: "0 0 15px 0", fontSize: "24px" }}>
          ⚠️ Error Loading Devices
        </h3>
        <p style={{ margin: "0 0 20px 0", fontSize: "16px" }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "12px 24px",
            backgroundColor: "#dc3545",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          🔄 Retry
        </button>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div
        style={{
          margin: "20px auto",
          maxWidth: "500px",
          textAlign: "center",
          padding: "40px",
          border: "2px dashed #6c757d",
          borderRadius: "12px",
          backgroundColor: "#f8f9fa",
          color: "#6c757d",
        }}
      >
        <h3 style={{ margin: "0 0 15px 0", fontSize: "24px" }}>
          📱 No Devices Found
        </h3>
        <p style={{ margin: "0", fontSize: "16px" }}>
          You don't have any Yoto devices registered yet.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "10px",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          marginBottom: "10px",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            margin: "0 0 10px 0",
            fontSize: "24px",
            color: "#333",
          }}
        >
          Your Yoto Players
        </h2>
        <p
          style={{
            margin: "0",
            color: "#666",
            fontSize: "16px",
          }}
        >
          Select devices to monitor battery levels
        </p>
      </div>

      {/* Devices Grid */}
      <div
        style={{
          display: "grid",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        {devices.map((device) => (
          <Device
            key={device.id}
            device={device}
            isSelected={selectedDevices.has(device.id)}
            onSelectionChange={(isSelected) =>
              handleDeviceSelect(device.id, isSelected)
            }
          />
        ))}
      </div>

      {/* Subscribe Section */}
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
        }}
      >
        <h3
          style={{
            margin: "0 0 15px 0",
            color: "#333",
            fontSize: "20px",
          }}
        >
          🔔 Battery Notifications
        </h3>

        {/* Battery Threshold Slider */}
        <div
          style={{
            padding: "20px",
            backgroundColor: "white",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>🔋</span>
            <span style={{ fontSize: "16px", color: "#333" }}>
              Notify below:
            </span>
            <span
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: getThresholdColor(batteryThreshold),
                minWidth: "45px",
              }}
            >
              {batteryThreshold}%
            </span>
          </div>

          <div style={{ position: "relative", margin: "15px 0" }}>
            <input
              type="range"
              min="1"
              max="99"
              step="1"
              value={batteryThreshold}
              onChange={(e) => setBatteryThreshold(parseInt(e.target.value))}
              style={{
                width: "100%",
                height: "8px",
                borderRadius: "4px",
                background: `linear-gradient(to right, 
                  ${getThresholdColor(batteryThreshold)} 0%, 
                  ${getThresholdColor(batteryThreshold)} ${batteryThreshold}%, 
                  #e9ecef ${batteryThreshold}%, 
                  #e9ecef 100%)`,
                outline: "none",
                appearance: "none",
                cursor: "pointer",
              }}
            />
            <style jsx>{`
              input[type="range"]::-webkit-slider-thumb {
                appearance: none;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: ${getThresholdColor(batteryThreshold)};
                cursor: pointer;
                border: 2px solid white;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
              }
              input[type="range"]::-moz-range-thumb {
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: ${getThresholdColor(batteryThreshold)};
                cursor: pointer;
                border: 2px solid white;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
              }
            `}</style>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#666",
              marginTop: "8px",
            }}
          >
            <span>1%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>99%</span>
          </div>
        </div>
        {/* Subscribe & Test Buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "15px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={handleSubscribe}
            style={{
              padding: "15px 30px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
              transition: "background-color 0.2s",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "#218838";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "#28a745";
            }}
          >
            🔔 Subscribe to {selectedDevices.size} device
            {selectedDevices.size === 1 ? "" : "s"}
          </button>

          <button
            onClick={handleTestNotification}
            style={{
              padding: "10px 20px",
              backgroundColor: "#e2e6ea",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              transition: "background-color 0.2s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "#d6d8db";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "#e2e6ea";
            }}
          >
            📤 Send Test Notification
          </button>
        </div>
      </div>
      <AlertPopover
        message={alertMessage}
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default DeviceList;
